import {
  CLIENT_HEADER,
  CLIENT_HEADER_VALUE,
  type ErrorCode,
  type FieldError,
  isProblem,
  type LoginResponse,
} from "@eduleo/shared";
import { useAuthStore } from "../stores/auth-store";

export const API_BASE = "/api/v1";

export type ApiErrorCode = ErrorCode | "NETWORK_ERROR";

/** Every failed call becomes an ApiError with a message that can be shown as-is. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly detail: string | undefined;
  readonly fieldErrors: FieldError[];
  readonly requestId: string | undefined;
  readonly retryAfterSeconds: number | undefined;

  constructor(options: {
    status: number;
    code: ApiErrorCode;
    title: string;
    detail?: string;
    fieldErrors?: FieldError[];
    requestId?: string;
    retryAfterSeconds?: number;
  }) {
    super(options.title);
    this.name = "ApiError";
    this.status = options.status;
    this.code = options.code;
    this.detail = options.detail;
    this.fieldErrors = options.fieldErrors ?? [];
    this.requestId = options.requestId;
    this.retryAfterSeconds = options.retryAfterSeconds;
  }
}

const NETWORK_ERROR = () =>
  new ApiError({
    status: 0,
    code: "NETWORK_ERROR",
    title: "Can't reach Eduleo",
    detail: "Check your internet connection and try again.",
  });

async function toApiError(response: Response): Promise<ApiError> {
  const retryAfter = Number(response.headers.get("retry-after"));
  const body: unknown = await response.json().catch(() => null);
  if (isProblem(body)) {
    return new ApiError({
      status: response.status,
      code: body.code,
      title: body.title,
      detail: body.detail,
      fieldErrors: body.errors,
      requestId: body.requestId,
      retryAfterSeconds: Number.isFinite(retryAfter) && retryAfter > 0 ? retryAfter : undefined,
    });
  }
  return new ApiError({
    status: response.status,
    code: "INTERNAL",
    title: "Something went wrong on our side",
    detail: "Please try again in a moment.",
  });
}

let refreshInFlight: Promise<boolean> | null = null;

/**
 * Swaps the refresh cookie for a new access token. Only one refresh runs at a time,
 * across every open tab (Web Locks), because the server revokes a session whose refresh
 * token is used twice.
 */
export function refreshSession(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    const run = async () => {
      try {
        return await fetch(`${API_BASE}/auth/refresh`, {
          method: "POST",
          credentials: "same-origin",
          headers: { [CLIENT_HEADER]: CLIENT_HEADER_VALUE },
        });
      } catch {
        return null;
      }
    };
    const response =
      typeof navigator !== "undefined" && navigator.locks
        ? await navigator.locks.request("eduleo-auth-refresh", run)
        : await run();
    if (response?.ok) {
      useAuthStore.getState().setSession((await response.json()) as LoginResponse);
      return true;
    }
    // Only a 401 means the session has ended. Network failures and server errors
    // (say, during a deploy) leave the person signed in so they can simply retry.
    if (response?.status === 401) useAuthStore.getState().clearSession();
    return false;
  })().finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  /** JSON-encoded, except FormData (file uploads), which is sent as multipart as-is. */
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  signal?: AbortSignal;
  /** Send the access token, and refresh it once if it has expired. Default true. */
  auth?: boolean;
  /** Cookie-authenticated auth endpoints need the client header. */
  clientHeader?: boolean;
  /** Extra request headers, e.g. the signing page's `x-signing-session`. */
  headers?: Record<string, string>;
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== "") params.set(key, String(value));
  }
  const search = params.toString();
  return `${API_BASE}${path}${search ? `?${search}` : ""}`;
}

/** Sends the request with the access token, refreshing it once if it has expired. */
async function sendRequest(
  path: string,
  options: RequestOptions,
  accept: string,
): Promise<Response> {
  const {
    method = "GET",
    body,
    query,
    signal,
    auth = true,
    clientHeader = false,
    headers: extraHeaders,
  } = options;
  const isForm = typeof FormData !== "undefined" && body instanceof FormData;

  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = { ...extraHeaders, accept };
    // The browser sets the multipart boundary itself, so FormData gets no content-type here.
    if (body !== undefined && !isForm) headers["content-type"] = "application/json";
    if (clientHeader) headers[CLIENT_HEADER] = CLIENT_HEADER_VALUE;
    const token = useAuthStore.getState().accessToken;
    if (auth && token) headers.authorization = `Bearer ${token}`;
    try {
      return await fetch(buildUrl(path, query), {
        method,
        headers,
        body: body === undefined ? undefined : isForm ? (body as FormData) : JSON.stringify(body),
        credentials: "same-origin",
        signal,
      });
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") throw error;
      throw NETWORK_ERROR();
    }
  };

  let response = await send();
  if (response.status === 401 && auth && useAuthStore.getState().accessToken) {
    if (await refreshSession()) response = await send();
  }
  if (!response.ok) {
    const error = await toApiError(response);
    // Someone else set this person's password: send them to choose their own (RequireAuth).
    if (error.code === "PASSWORD_CHANGE_REQUIRED") useAuthStore.getState().requirePasswordChange();
    throw error;
  }
  return response;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await sendRequest(path, options, "application/json");
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

/** Downloads a file that needs the access token (so a plain link won't do). */
export async function apiDownload(path: string, options: RequestOptions = {}): Promise<Blob> {
  return (await apiDownloadFile(path, options)).blob;
}

/** The file and the name the server gave it (Content-Disposition), if any. */
export async function apiDownloadFile(
  path: string,
  options: RequestOptions = {},
): Promise<{ blob: Blob; fileName: string | null }> {
  const response = await sendRequest(path, options, "*/*");
  return {
    blob: await response.blob(),
    fileName: fileNameFrom(response.headers.get("content-disposition")),
  };
}

/** `attachment; filename="a.pdf"; filename*=UTF-8''a.pdf` → "a.pdf" (the UTF-8 form wins). */
export function fileNameFrom(disposition: string | null): string | null {
  if (!disposition) return null;
  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)?.[1];
  if (encoded) {
    try {
      return decodeURIComponent(encoded.trim());
    } catch {
      // Fall back to the plain name below.
    }
  }
  return /filename="([^"]*)"/i.exec(disposition)?.[1] ?? null;
}

/** A message for people, from any thrown value. */
export function describeError(error: unknown): { title: string; detail?: string } {
  if (error instanceof ApiError) return { title: error.message, detail: error.detail };
  return { title: "Something went wrong", detail: "Please try again." };
}
